import React, { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTheme } from "@/studio/lib/theme";
import { usePerf } from "@/studio/lib/perf";

const SRC = "/blackhole.webp";

/**
 * Parabolum brand object — alive, but quiet.
 *
 * Final creative direction (Feb 2026):
 *  • Event horizon (the image) — that's the object
 *  • Tiny breathing: scale 1 ↔ 1.018 over 9s (was 1.035 over 7s)
 *  • Small shimmer: opacity 0.85 ↔ 1.0 in the same loop
 *  • No constant spin, no orbit ring, no HUD, no debug overlays
 *  • No lens warp by default, no particles by default
 *  • No heavy glow — a single, very soft ambient halo, low opacity
 *  • Cursor parallax kept (≤ parallax px) — it's an anchor, not a gimmick
 */
export default function BlackHole({
  className = "",
  style,
  parallax = 14,
  idle = true,
  glow = false,
  priority = false,
  scrollPulse = 0,
}) {
  const reduced = useReducedMotion();
  const { theme } = useTheme();
  const caps = usePerf();
  const enableParallax = caps.parallax;
  const wrapRef = useRef(null);

  // gentle pointer parallax — no lens, no warp
  useEffect(() => {
    if (reduced || !enableParallax) return undefined;
    const el = wrapRef.current;
    if (!el) return undefined;
    let raf;
    let tx = 0;
    let ty = 0;
    let cx = 0;
    let cy = 0;
    const onMove = (e) => {
      tx = (e.clientX / window.innerWidth - 0.5) * parallax;
      ty = (e.clientY / window.innerHeight - 0.5) * parallax;
    };
    const onLeave = () => { tx = 0; ty = 0; };
    const loop = () => {
      cx += (tx - cx) * 0.05;
      cy += (ty - cy) * 0.05;
      el.style.setProperty("--px", `${cx.toFixed(2)}px`);
      el.style.setProperty("--py", `${cy.toFixed(2)}px`);
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerleave", onLeave);
    raf = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, [reduced, parallax, enableParallax]);

  const breathe = idle && !reduced;
  // gentle scrollPulse: ≤1% scale bump, never spinning
  const pulse = 1 + scrollPulse * 0.012;
  // dark theme uses a colder white shimmer, light theme uses a warm one
  const haloColor =
    theme === "dark"
      ? "rgba(228, 255, 150, 0.10)"
      : "rgba(40, 40, 60, 0.08)";

  return (
    <div
      ref={wrapRef}
      className={`pointer-events-none relative flex items-center justify-center ${className}`}
      style={{ ...style }}
    >
      <div
        style={{ transform: "translate3d(var(--px,0), var(--py,0), 0)" }}
        className="relative h-full w-full"
      >
        {/* Single quiet ambient halo — no blur stack, no ring, no orbit */}
        {glow && (
          <motion.div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              width: "70%",
              height: "70%",
              background: `radial-gradient(circle, ${haloColor} 0%, rgba(0,0,0,0) 62%)`,
              filter: "blur(6px)",
              mixBlendMode: "screen",
            }}
            animate={breathe ? { opacity: [0.55, 0.85, 0.55] } : { opacity: 0.7 }}
            transition={{ duration: 9, ease: "easeInOut", repeat: breathe ? Infinity : 0 }}
          />
        )}

        <motion.img
          src={SRC}
          alt=""
          aria-hidden="true"
          draggable="false"
          loading={priority ? "eager" : "lazy"}
          className="relative h-full w-full select-none object-contain"
          style={{
            willChange: "transform, opacity",
            transform: `scale(${pulse})`,
          }}
          animate={
            breathe
              ? { scale: [1, 1.018, 1], opacity: [0.92, 1, 0.92] }
              : {}
          }
          transition={{ duration: 9, ease: "easeInOut", repeat: Infinity }}
        />
      </div>
    </div>
  );
}
